/**
 * Hangman Duel Cartridge Implementation
 */
import { HangmanEngine, MAX_STRIKES } from './engine.js';
import { WORD_BANKS } from './words.js';
import { HangmanRenderer } from './renderer.js';

const KEYBOARD_ROWS = [
  ['Q', 'W', 'E', 'R', 'T', 'Y', 'U', 'I', 'O', 'P'],
  ['A', 'S', 'D', 'F', 'G', 'H', 'J', 'K', 'L'],
  ['Z', 'X', 'C', 'V', 'B', 'N', 'M']
];

export const Cartridge = {
  id: 'hangman',
  name: 'Hangman Duel',
  tagline: 'Word deduction race with curated categories',
  icon: '🔤💀',
  category: 'Word',
  minPlayers: 1,
  maxPlayers: 2,
  modes: ['solo', 'multiplayer'],

  defaultConfig: {
    category: 'animals',
    difficulty: 'medium'
  },

  configOptions: [
    {
      key: 'category',
      label: 'Word Category',
      type: 'select',
      options: Object.entries(WORD_BANKS).map(([k, v]) => ({ value: k, label: `${v.icon} ${v.name}` }))
    },
    {
      key: 'difficulty',
      label: 'Word Difficulty',
      type: 'select',
      options: [
        { value: 'easy', label: 'Easy (Short Words)' },
        { value: 'medium', label: 'Medium (Standard)' },
        { value: 'hard', label: 'Hard (Long / Rare)' }
      ]
    }
  ],

  async init(mountElement, context) {
    this.mountElement = mountElement;
    this.context = context;
    const cat = context.config?.category || 'animals';
    const diff = context.config?.difficulty || 'medium';

    this.engine = new HangmanEngine(cat, diff);
    this.opponentStrikes = 0;
    this.opponentRevealedCount = 0;
    this.isOver = false;

    // Symmetrical word sync for multiplayer: host transmits secret word seed
    if (context.mode === 'multiplayer' && context.isHost) {
      context.sendAction('set_word', {
        word: this.engine.word,
        category: cat,
        difficulty: diff
      });
    }

    this.buildDOM();
    this.renderer = new HangmanRenderer(this.canvas);
    this.renderer.resize();

    window.addEventListener('resize', this.boundResize = () => {
      if (this.renderer) {
        this.renderer.resize();
        this.renderer.render(this.engine.strikes, this.engine.isWon, this.engine.isLost);
      }
    });

    window.addEventListener('keydown', this.boundKeydown = (e) => {
      const char = e.key.toUpperCase();
      if (/^[A-Z]$/.test(char) && !this.isOver) {
        this.handleLetterGuess(char);
      }
    });

    this.render();
  },

  buildDOM() {
    this.mountElement.innerHTML = `
      <div class="hangman-wrapper" style="display:flex; flex-direction:column; align-items:center; width:100%; max-width:540px; margin:0 auto;">
        <div class="game-hud">
          <div class="hud-player">
            <span>🔤 ${this.engine.categoryName}</span>
          </div>
          <div id="hm-turn-badge" class="hud-turn-indicator active">Strikes: 0/${MAX_STRIKES}</div>
          <div class="hud-player">
            <span id="hm-opp-status">${this.context.mode === 'solo' ? 'Solo Mode' : 'Opponent: 0/6'}</span>
          </div>
        </div>

        <div style="display:flex; justify-content:center; margin:8px 0;">
          <canvas id="hangman-canvas" style="touch-action:none;"></canvas>
        </div>

        <!-- Word Blanks Display -->
        <div id="hm-word-display" style="display:flex; gap:8px; justify-content:center; flex-wrap:wrap; margin:16px 0; min-height:48px;">
          <!-- Letters inserted dynamically -->
        </div>

        <!-- On-Screen Keyboard -->
        <div id="hm-keyboard" style="display:flex; flex-direction:column; gap:6px; width:100%; max-width:440px; margin-top:8px;">
          ${KEYBOARD_ROWS.map((row) => `
            <div style="display:flex; gap:4px; justify-content:center; width:100%;">
              ${row.map((l) => `
                <button class="hm-key-btn btn btn-secondary btn-sm" data-letter="${l}" style="flex:1; padding:10px 0; font-weight:800; font-size:1rem; min-height:44px;">
                  ${l}
                </button>
              `).join('')}
            </div>
          `).join('')}
        </div>
      </div>
    `;

    this.canvas = this.mountElement.querySelector('#hangman-canvas');
    this.turnBadge = this.mountElement.querySelector('#hm-turn-badge');
    this.oppStatus = this.mountElement.querySelector('#hm-opp-status');
    this.wordDisplay = this.mountElement.querySelector('#hm-word-display');

    this.mountElement.querySelectorAll('.hm-key-btn').forEach((btn) => {
      btn.addEventListener('click', () => {
        const letter = btn.getAttribute('data-letter');
        this.handleLetterGuess(letter);
      });
    });
  },

  handleLetterGuess(letter) {
    if (this.isOver) return;
    if (this.engine.guessedLetters.has(letter)) {
      this.context.audio?.playSfx('tap');
      return;
    }

    const res = this.engine.guessLetter(letter);
    if (!res) return;

    if (res.inWord) {
      this.context.audio?.playSfx('place');
    } else {
      this.context.audio?.playSfx('miss');
    }

    // Sync progress in multiplayer
    if (this.context.mode === 'multiplayer') {
      this.context.sendAction('progress', {
        strikes: this.engine.strikes,
        revealed: this.engine.getLettersRevealedCount(),
        isWon: this.engine.isWon,
        isLost: this.engine.isLost
      });
    }

    this.render();

    if (this.engine.isWon) {
      this.handleGameOver(true, `Splendid! You guessed "${this.engine.word}"!`);
    } else if (this.engine.isLost) {
      this.handleGameOver(false, `Hanged! The secret word was "${this.engine.word}".`);
    }
  },

  handleRemoteAction(action) {
    if (action.actionType === 'set_word' && !this.context.isHost) {
      // Guest rehydrates identical word from host
      this.engine.reset(action.payload.word);
      this.render();
    } else if (action.actionType === 'progress') {
      const { strikes, revealed, isWon, isLost } = action.payload;
      this.opponentStrikes = strikes;
      this.opponentRevealedCount = revealed;

      if (this.oppStatus) {
        this.oppStatus.textContent = `Opponent: ${strikes}/6 (${revealed} letters)`;
      }

      if (isWon && !this.isOver) {
        // Opponent solved first!
        this.handleGameOver(false, `Opponent solved the word first! It was "${this.engine.word}".`);
      } else if (isLost && !this.isOver) {
        // Opponent hanged!
        this.handleGameOver(true, `Opponent was hanged! You win! Word was "${this.engine.word}".`);
      }
    }
  },

  handleGameOver(isWin, reason) {
    this.isOver = true;
    this.context.audio?.playSfx(isWin ? 'victory' : 'defeat');
    this.context.onGameOver?.({
      winnerId: isWin ? this.context.playerId : this.context.opponentId,
      isDraw: false,
      reason,
      stats: { strikes: this.engine.strikes, word: this.engine.word }
    });
    this.render();
  },

  render() {
    if (this.renderer) {
      this.renderer.render(this.engine.strikes, this.engine.isWon, this.engine.isLost);
    }

    if (this.turnBadge) {
      this.turnBadge.textContent = `Strikes: ${this.engine.strikes}/${MAX_STRIKES}`;
      this.turnBadge.className = this.engine.isLost ? 'hud-turn-indicator waiting' : 'hud-turn-indicator active';
    }

    // Render Word Blanks
    this.wordDisplay.innerHTML = '';
    const masked = this.engine.getMaskedWord();
    for (const char of masked) {
      const span = document.createElement('span');
      span.textContent = char === '_' ? '' : char;
      span.style.cssText = `
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 38px;
        height: 48px;
        border-bottom: 3px solid ${char === '_' ? '#64748b' : '#f59e0b'};
        font-family: var(--font-mono);
        font-size: 1.6rem;
        font-weight: 800;
        color: ${char === '_' ? 'transparent' : '#f8fafc'};
      `;
      this.wordDisplay.appendChild(span);
    }

    // Update Keyboard Button States
    this.mountElement.querySelectorAll('.hm-key-btn').forEach((btn) => {
      const letter = btn.getAttribute('data-letter');
      if (this.engine.guessedLetters.has(letter)) {
        btn.disabled = true;
        const inWord = this.engine.word.includes(letter);
        btn.style.opacity = '0.4';
        btn.style.background = inWord ? '#065f46' : '#7f1d1d';
        btn.style.color = '#fff';
      }
    });
  },

  serializeState() {
    return {
      engine: this.engine.serialize(),
      isOver: this.isOver,
      opponentStrikes: this.opponentStrikes
    };
  },

  restoreState(state) {
    if (state?.engine) {
      this.engine.restore(state.engine);
      this.isOver = !!state.isOver;
      this.render();
    }
  },

  pause() {},
  resume() {},

  destroy() {
    if (this.boundResize) window.removeEventListener('resize', this.boundResize);
    if (this.boundKeydown) window.removeEventListener('keydown', this.boundKeydown);
  }
};
